/**
 * ContactsPanel — «¿a quién llamo si esto falla?» dentro de cualquier ficha.
 *
 * Se monta en la ficha de un servidor, una VM, un servicio, una cámara… y
 * lista los contactos vinculados a ESE elemento, con su papel (responsable,
 * soporte, escalado, proveedor…).
 *
 * Tres acciones:
 *  · **Vincular** un contacto que ya existe en la empresa (con papel y
 *    «principal»).
 *  · **Crear y vincular** uno nuevo sin salir de la ficha (nombre + papel +
 *    correo/teléfono).
 *  · **Quitar** el vínculo (no borra el contacto).
 *
 * El vínculo es polimórfico (`object_type` + `object_id`), igual que las notas
 * y los documentos: no hay que tocar el modelo de cada CI.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  UserGroupIcon,
  UserPlusIcon,
  LinkIcon,
  TrashIcon,
  EnvelopeIcon,
  PhoneIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import Dialog from './common/Dialog';

const RELACIONES = [
  { value: 'owner', label: 'Responsable' },
  { value: 'support', label: 'Soporte' },
  { value: 'escalation', label: 'Escalado' },
  { value: 'provider', label: 'Proveedor' },
  { value: 'billing', label: 'Facturación' },
  { value: 'other', label: 'Otro' },
];

const TONO_RELACION = {
  owner: 'list-badge--accent',
  support: 'list-badge--on',
  escalation: 'list-badge--warn',
  provider: 'list-badge',
  billing: 'list-badge--muted',
  other: 'list-badge--muted',
};

export default function ContactsPanel({
  companyId,
  objectType,
  objectId,
  objectName,
  compact = false,
}) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [modo, setModo] = useState('existente');
  const [contactos, setContactos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    contact_id: '',
    relation: 'owner',
    is_primary: false,
    name: '',
    email: '',
    phone: '',
    role: '',
  });

  const cargar = useCallback(async () => {
    if (!companyId || !objectType || !objectId) return;
    setLoading(true);
    setError('');
    try {
      const data = await api.getContactsForObject(objectType, objectId, companyId);
      setLinks(data || []);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los contactos');
    } finally {
      setLoading(false);
    }
  }, [companyId, objectType, objectId]);

  useEffect(() => { cargar(); }, [cargar]);

  const abrir = async () => {
    setDialogOpen(true);
    setModo('existente');
    setForm({ contact_id: '', relation: 'owner', is_primary: links.length === 0, name: '', email: '', phone: '', role: '' });
    try {
      const res = await api.getContacts(companyId, { active_only: true });
      setContactos(res.data || []);
    } catch {
      setContactos([]);
    }
  };

  const vincular = async () => {
    if (!form.contact_id) return;
    setBusy(true);
    setError('');
    try {
      await api.addContactLink(form.contact_id, {
        object_type: objectType,
        object_id: objectId,
        relation: form.relation,
        is_primary: form.is_primary,
      }, companyId);
      setDialogOpen(false);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo vincular el contacto');
    } finally {
      setBusy(false);
    }
  };

  const crearYVincular = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    setError('');
    try {
      const contacto = await api.createContact({
        name: form.name,
        email: form.email,
        phone: form.phone,
        role: form.role,
      }, companyId);
      await api.addContactLink(contacto.id, {
        object_type: objectType,
        object_id: objectId,
        relation: form.relation,
        is_primary: form.is_primary,
      }, companyId);
      setDialogOpen(false);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo crear el contacto');
    } finally {
      setBusy(false);
    }
  };

  const quitar = async (link) => {
    try {
      await api.deleteContactLink(link.contact_id, link.id, companyId);
      await cargar();
    } catch (err) {
      setError(err.message || 'No se pudo quitar el vínculo');
    }
  };

  const nombreContacto = (link) => link.contact?.name || link.contact_name || `Contacto #${link.contact_id}`;
  const ordenados = useMemo(
    () => [...links].sort((a, b) => Number(b.is_primary) - Number(a.is_primary)),
    [links],
  );

  return (
    <div className="contacts-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <UserGroupIcon style={{ width: 16, height: 16, color: 'var(--text-secondary)' }} />
        <span style={{ fontWeight: 'var(--fw-semibold)', fontSize: '0.84rem' }}>
          Contactos {links.length > 0 ? `(${links.length})` : ''}
        </span>
        <button
          type="button"
          className="list-btn-secondary"
          style={{ marginLeft: 'auto', padding: '3px 10px', fontSize: '0.76rem' }}
          onClick={abrir}
        >
          <UserPlusIcon style={{ width: 13, height: 13 }} /> Vincular
        </button>
      </div>

      {error && <p style={{ color: 'var(--danger, #ef4444)', fontSize: '0.78rem', margin: '0 0 6px' }}>{error}</p>}
      {loading && links.length === 0 && <p className="list-hint" style={{ margin: 0 }}>Cargando contactos…</p>}
      {!loading && links.length === 0 && (
        <p className="list-hint" style={{ margin: 0 }}>
          Sin contactos asociados. Vincula a quién avisar si este elemento falla.
        </p>
      )}

      {ordenados.map((link) => (
        <div
          key={link.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 0',
            borderTop: '1px solid var(--border)',
            flexWrap: 'wrap',
          }}
        >
          <span className={`list-badge ${TONO_RELACION[link.relation] || 'list-badge'}`}>
            {link.relation_label || link.relation}
          </span>
          <strong style={{ fontSize: '0.84rem' }}>{nombreContacto(link)}</strong>
          {link.is_primary && (
            <StarIcon style={{ width: 13, height: 13, color: 'var(--accent)' }} title="Contacto principal" />
          )}
          {link.contact?.role && (
            <span className="list-hint">{link.contact.role}</span>
          )}
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
            {link.contact?.email && (
              <a href={`mailto:${link.contact.email}`} title={link.contact.email} className="list-hint">
                <EnvelopeIcon style={{ width: 14, height: 14 }} />
              </a>
            )}
            {link.contact?.phone && (
              <a href={`tel:${link.contact.phone}`} title={link.contact.phone} className="list-hint">
                <PhoneIcon style={{ width: 14, height: 14 }} />
              </a>
            )}
            <button
              type="button"
              className="list-btn-secondary"
              style={{ padding: '2px 7px', fontSize: '0.72rem' }}
              onClick={() => quitar(link)}
              title="Quitar el vínculo (no borra el contacto)"
            >
              <TrashIcon style={{ width: 12, height: 12 }} />
            </button>
          </span>
        </div>
      ))}

      {dialogOpen && (
        <Dialog
          title={`Vincular contacto${objectName ? ` a ${objectName}` : ''}`}
          icon={<LinkIcon style={{ width: 18, height: 18 }} />}
          maxWidth={560}
          busy={busy}
          onClose={() => setDialogOpen(false)}
          footer={(
            <>
              <button type="button" className="list-btn-secondary" onClick={() => setDialogOpen(false)} disabled={busy}>
                Cancelar
              </button>
              <button
                type="button"
                className="list-btn-primary"
                onClick={modo === 'existente' ? vincular : crearYVincular}
                disabled={busy || (modo === 'existente' ? !form.contact_id : !form.name.trim())}
              >
                {busy ? 'Guardando…' : (modo === 'existente' ? 'Vincular' : 'Crear y vincular')}
              </button>
            </>
          )}
        >
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <button
              type="button"
              className={modo === 'existente' ? 'list-btn-primary' : 'list-btn-secondary'}
              style={{ fontSize: '0.78rem' }}
              onClick={() => setModo('existente')}
            >
              Contacto existente
            </button>
            <button
              type="button"
              className={modo === 'nuevo' ? 'list-btn-primary' : 'list-btn-secondary'}
              style={{ fontSize: '0.78rem' }}
              onClick={() => setModo('nuevo')}
            >
              Crear nuevo
            </button>
          </div>

          {modo === 'existente' ? (
            <label className="list-hint" style={{ display: 'block', marginBottom: 10 }}>
              Contacto
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 4 }}
                value={form.contact_id}
                onChange={(event) => setForm((prev) => ({ ...prev, contact_id: event.target.value }))}
              >
                <option value="">— elige un contacto —</option>
                {contactos.map((contacto) => (
                  <option key={contacto.id} value={contacto.id}>
                    {contacto.name}{contacto.role ? ` · ${contacto.role}` : ''}{contacto.email ? ` · ${contacto.email}` : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
              <label className="list-hint" style={{ gridColumn: '1 / -1' }}>
                Nombre *
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 3 }}
                  value={form.name}
                  onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                />
              </label>
              <label className="list-hint">
                Correo
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 3 }}
                  value={form.email}
                  onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                />
              </label>
              <label className="list-hint">
                Teléfono
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 3 }}
                  value={form.phone}
                  onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
                />
              </label>
              <label className="list-hint" style={{ gridColumn: '1 / -1' }}>
                Puesto
                <input
                  className="list-input"
                  style={{ width: '100%', marginTop: 3 }}
                  value={form.role}
                  onChange={(event) => setForm((prev) => ({ ...prev, role: event.target.value }))}
                />
              </label>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label className="list-hint" style={{ flex: '1 1 180px' }}>
              Papel
              <select
                className="list-select"
                style={{ width: '100%', marginTop: 3 }}
                value={form.relation}
                onChange={(event) => setForm((prev) => ({ ...prev, relation: event.target.value }))}
              >
                {RELACIONES.map((relacion) => (
                  <option key={relacion.value} value={relacion.value}>{relacion.label}</option>
                ))}
              </select>
            </label>
            <label className="list-hint" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="checkbox"
                checked={form.is_primary}
                onChange={(event) => setForm((prev) => ({ ...prev, is_primary: event.target.checked }))}
              />
              Contacto principal
            </label>
          </div>
          {compact && <p className="list-hint" style={{ marginTop: 10 }}>El contacto se crea en la empresa y queda disponible para los demás elementos.</p>}
        </Dialog>
      )}
    </div>
  );
}
